import { create } from 'zustand';
import type { UserPublic, SitePublic, SubscriptionPublic } from '@wooconnect/shared';
import { Sentry } from '@/lib/sentry';
import { apiClient } from '@/services/api';

interface AuthState {
  user: UserPublic | null;
  subscription: SubscriptionPublic | null;
  sites: SitePublic[];
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadSession: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  setUser: (user: UserPublic) => void;
  setSites: (sites: SitePublic[]) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  subscription: null,
  sites: [],
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    const res = await apiClient.post('/auth/login', { email, password });
    const { accessToken, refreshToken, user } = res.data.data;
    localStorage.setItem('wca_access_token', accessToken);
    localStorage.setItem('wca_refresh_token', refreshToken);
    set({ user, isAuthenticated: true });
    Sentry.setUser({ id: user.id, email: user.email });

    // Load sites
    try {
      const sitesRes = await apiClient.get('/sites');
      set({ sites: sitesRes.data.data?.items ?? sitesRes.data.data ?? [] });
    } catch {
      // Non-critical
    }

    // Load subscription
    try {
      const meRes = await apiClient.get('/auth/me');
      if (meRes.data.data?.subscription) {
        set({ subscription: meRes.data.data.subscription });
      }
    } catch {
      // Non-critical
    }
  },

  logout: async () => {
    try {
      const refreshToken = localStorage.getItem('wca_refresh_token');
      if (refreshToken) {
        await apiClient.post('/auth/logout', { refreshToken }).catch(() => {});
      }
    } finally {
      localStorage.removeItem('wca_access_token');
      localStorage.removeItem('wca_refresh_token');
      set({ user: null, subscription: null, sites: [], isAuthenticated: false });
      Sentry.setUser(null);
    }
  },

  loadSession: async () => {
    const token = localStorage.getItem('wca_access_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const meRes = await apiClient.get('/auth/me');
      const user = meRes.data.data.user ?? meRes.data.data;
      set({ user, isAuthenticated: true, subscription: meRes.data.data?.subscription ?? null });
      Sentry.setUser({ id: user.id, email: user.email });

      const sitesRes = await apiClient.get('/sites');
      set({ sites: sitesRes.data.data?.items ?? sitesRes.data.data ?? [] });
    } catch {
      localStorage.removeItem('wca_access_token');
      localStorage.removeItem('wca_refresh_token');
      set({ user: null, isAuthenticated: false });
      Sentry.setUser(null);
    } finally {
      set({ isLoading: false });
    }
  },

  refreshSession: async () => {
    const refreshToken = localStorage.getItem('wca_refresh_token');
    if (!refreshToken) return false;

    try {
      const res = await apiClient.post('/auth/refresh', { refreshToken });
      const { accessToken, refreshToken: newRefresh } = res.data.data;
      localStorage.setItem('wca_access_token', accessToken);
      localStorage.setItem('wca_refresh_token', newRefresh);
      return true;
    } catch {
      localStorage.removeItem('wca_access_token');
      localStorage.removeItem('wca_refresh_token');
      set({ user: null, isAuthenticated: false });
      return false;
    }
  },

  setUser: (user) => set({ user }),
  setSites: (sites) => set({ sites }),
}));
